import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../../lib/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';

export const ModulesTab = ({ tenantId: defaultTenantId, role }) => {
  const qc = useQueryClient();
  const [pickedTenant, setPickedTenant] = useState('');
  const tenantId = pickedTenant || defaultTenantId;
  const { data: allTenants } = useQuery({
    queryKey: ['modules-tenants'], retry: false, enabled: role === 'global_admin',
    queryFn: () => api.get('/tenants').then((r) => r.data).catch(() => []),
  });
  const schoolTenants = (allTenants || []).filter((t) => t.type !== 'org');
  const { data: modCatalog } = useQuery({
    queryKey: ['modules-catalog', tenantId],
    queryFn: () => api.get('/modules/catalog', { params: { tenant_id: tenantId } }).then((r) => r.data),
    enabled: !!tenantId,
  });
  const inval = () => { qc.invalidateQueries({ queryKey: ['modules-catalog'] }); qc.invalidateQueries({ queryKey: ['modules-enabled'] }); };
  const modToggleMut = useMutation({
    mutationFn: ({ module, enabled }) => api.post('/modules/toggle', { tenant_id: tenantId, module, enabled }),
    onSuccess: (r) => { toast.success(`${r.data.module} ${r.data.enabled ? 'enabled' : 'revoked'}`); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const trialMut = useMutation({
    mutationFn: (module) => api.post('/modules/trial', { tenant_id: tenantId, module, days: 30 }),
    onSuccess: (r) => { toast.success(`30-day ${r.data.module} trial started - expires ${r.data.expires_at.slice(0, 10)}`); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const channelMut = useMutation({
    mutationFn: ({ module, channel }) => api.post('/modules/channel', { module, channel }),
    onSuccess: (r) => { toast.success(`${r.data.module} is now ${r.data.channel === 'ga' ? 'GA (all tenants)' : 'Beta (sandbox tenants only)'}`); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const envMut = useMutation({
    mutationFn: (environment) => api.post('/modules/tenant-environment', { tenant_id: tenantId, environment }),
    onSuccess: (r) => { toast.success(`Tenant environment: ${r.data.environment}`); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const env = modCatalog?.[0]?.tenant_environment || 'production';

  return (
    <div className="max-w-2xl bg-white border border-slate-200 rounded-lg p-6" data-testid="modules-panel">
      <h2 className="font-display text-lg font-semibold mb-1">Module entitlements</h2>
      {role === 'global_admin' && schoolTenants.length > 0 && (
        <div className="flex items-center gap-2 mb-2" data-testid="modules-tenant-picker-row">
          <span className="font-mono-label text-[10px] uppercase text-slate-400">Institution</span>
          <select data-testid="modules-tenant-picker" value={tenantId || ''} onChange={(e) => setPickedTenant(e.target.value)}
            className="text-xs border border-slate-200 rounded px-2 py-1 bg-white">
            {schoolTenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
      )}
      <div className="flex items-center gap-2 mb-2" data-testid="tenant-environment-row">
        <span className="font-mono-label text-[10px] uppercase text-slate-400">Tenant environment</span>
        <span className={`font-mono-label text-[9px] uppercase px-2 py-0.5 rounded ${env === 'sandbox' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-600'}`} data-testid="tenant-environment-badge">{env}</span>
        {role === 'global_admin' && (
          <button data-testid="tenant-environment-toggle" onClick={() => envMut.mutate(env === 'sandbox' ? 'production' : 'sandbox')}
            className="text-xs underline text-slate-500 hover:text-slate-900">switch to {env === 'sandbox' ? 'production' : 'sandbox (dev)'}</button>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-4">Enable or revoke modules per institution based on what they've purchased. Revoked modules disappear from navigation and their APIs are blocked. {role !== 'global_admin' && 'Only the platform admin can change entitlements - this is your read-only license view.'}</p>
      <div className="divide-y divide-slate-100">
        {(modCatalog || []).map((m) => (
          <div key={m.key} className="py-2.5 flex items-center gap-3" data-testid={`module-row-${m.key}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{m.label}</p>
              <p className="font-mono-label text-[9px] uppercase text-slate-400">{m.category}{(m.requires || []).length ? ` · needs ${m.requires.join(', ')}` : ''}{(m.required_by || []).length ? ` · needed by ${m.required_by.join(', ')}` : ''}</p>
            </div>
            <span className={`font-mono-label text-[9px] uppercase px-2 py-0.5 rounded ${m.enabled ? 'bg-green-100 text-green-800' : 'bg-rose-100 text-rose-700'}`} data-testid={`module-status-${m.key}`}>{m.enabled ? 'enabled' : 'revoked'}</span>
            {m.channel === 'beta' && (
              <span className="font-mono-label text-[9px] uppercase px-2 py-0.5 rounded bg-blue-100 text-blue-800" data-testid={`module-beta-badge-${m.key}`}>beta</span>
            )}
            {m.trial && !m.trial.expired && (
              <span className="font-mono-label text-[9px] uppercase px-2 py-0.5 rounded bg-orange-100 text-orange-700" data-testid={`module-trial-badge-${m.key}`}>Trial - {m.trial.days_left}d left</span>
            )}
            {m.trial?.expired && (
              <span className="font-mono-label text-[9px] uppercase px-2 py-0.5 rounded bg-rose-100 text-rose-700" data-testid={`module-trial-expired-${m.key}`}>trial expired</span>
            )}
            {role === 'global_admin' && (
              <>
                <button data-testid={`module-channel-${m.key}`} onClick={() => channelMut.mutate({ module: m.key, channel: m.channel === 'beta' ? 'ga' : 'beta' })}
                  className="text-[10px] underline text-slate-400 hover:text-slate-900">{m.channel === 'beta' ? 'Promote to GA' : 'Mark beta'}</button>
                {!(m.trial && !m.trial.expired) && (
                  <Button data-testid={`module-trial-${m.key}`} size="sm" variant="outline" className="text-orange-700 border-orange-200 hover:bg-orange-50"
                    onClick={() => trialMut.mutate(m.key)}>30-day trial</Button>
                )}
                <Button data-testid={`module-toggle-${m.key}`} size="sm" variant="outline"
                  onClick={() => modToggleMut.mutate({ module: m.key, enabled: !(m.enabled && !m.trial) })}>
                  {m.enabled && !m.trial ? 'Revoke' : m.trial ? 'Purchase' : 'Enable'}
                </Button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
